import { apiKeysService } from '@/services/apiKeysService';
import { useCallback, useState } from 'react';

/**
 * Custom hook for managing API keys
 * @param {string} projectId - Project ID
 * @returns {Object} API keys state and operations
 */
export const useApiKeys = projectId => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  /**
   * Fetch API keys from the server
   */
  const fetchApiKeys = useCallback(
    async (page = 1, limit = 10, isActive = null) => {
      if (!projectId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await apiKeysService.getApiKeys(
          projectId,
          page,
          limit,
          isActive
        );

        if (response.success) {
          setApiKeys(response.data.apiKeys);
          setPagination({
            page: response.data.page,
            limit: response.data.limit,
            total: response.data.total,
            pages: response.data.pages,
          });
        } else {
          setError(response.error?.message || 'Failed to fetch API keys');
        }
      } catch (err) {
        console.error('Error fetching API keys:', err);
        setError(err.message || 'Failed to fetch API keys');
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Create a new API key
   */
  const createApiKey = useCallback(
    async (name, description = '') => {
      if (!projectId) throw new Error('Project ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await apiKeysService.createApiKey(
          projectId,
          name,
          description
        );

        if (response.success) {
          // Refresh the list to include the new API key
          await fetchApiKeys(pagination.page, pagination.limit);
          return response.data.apiKey;
        } else {
          throw new Error(
            response.error?.message || 'Failed to create API key'
          );
        }
      } catch (err) {
        console.error('Error creating API key:', err);
        setError(err.message || 'Failed to create API key');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId, fetchApiKeys, pagination.page, pagination.limit]
  );

  /**
   * Update an existing API key
   */
  const updateApiKey = useCallback(
    async (apiKeyId, name, description = '') => {
      if (!projectId) throw new Error('Project ID is required');
      if (!apiKeyId) throw new Error('API Key ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await apiKeysService.updateApiKey(
          apiKeyId,
          projectId,
          name,
          description
        );

        if (response.success) {
          // Update the API key in the local state
          setApiKeys(prev =>
            prev.map(key => {
              const keyId = key.id || key._id || key.apiKeyId;
              return keyId === apiKeyId ? response.data.apiKey : key;
            })
          );
          return response.data.apiKey;
        } else {
          throw new Error(
            response.error?.message || 'Failed to update API key'
          );
        }
      } catch (err) {
        console.error('Error updating API key:', err);
        setError(err.message || 'Failed to update API key');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Delete an API key
   */
  const deleteApiKey = useCallback(
    async apiKeyId => {
      if (!projectId) throw new Error('Project ID is required');
      if (!apiKeyId) throw new Error('API Key ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await apiKeysService.deleteApiKey(apiKeyId, projectId);

        if (response.success) {
          // Remove the API key from local state
          setApiKeys(prev =>
            prev.filter(key => {
              const keyId = key.id || key._id || key.apiKeyId;
              return keyId !== apiKeyId;
            })
          );
          return response;
        } else {
          throw new Error(
            response.error?.message || 'Failed to delete API key'
          );
        }
      } catch (err) {
        console.error('Error deleting API key:', err);
        setError(err.message || 'Failed to delete API key');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Regenerate an API key
   */
  const regenerateApiKey = useCallback(
    async apiKeyId => {
      if (!projectId) throw new Error('Project ID is required');
      if (!apiKeyId) throw new Error('API Key ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await apiKeysService.regenerateApiKey(
          apiKeyId,
          projectId
        );

        if (response.success) {
          // Update the API key in the local state
          setApiKeys(prev =>
            prev.map(key => {
              const keyId = key.id || key._id || key.apiKeyId;
              return keyId === apiKeyId ? response.data.apiKey : key;
            })
          );
          return response.data.apiKey;
        } else {
          throw new Error(
            response.error?.message || 'Failed to regenerate API key'
          );
        }
      } catch (err) {
        console.error('Error regenerating API key:', err);
        setError(err.message || 'Failed to regenerate API key');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Get a specific API key by ID
   */
  const getApiKeyById = useCallback(
    async apiKeyId => {
      if (!projectId) throw new Error('Project ID is required');

      try {
        const response = await apiKeysService.getApiKeyById(
          apiKeyId,
          projectId
        );

        if (response.success) {
          return response.data.apiKey;
        } else {
          throw new Error(response.error?.message || 'Failed to fetch API key');
        }
      } catch (err) {
        console.error('Error fetching API key:', err);
        throw err;
      }
    },
    [projectId]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Load API keys on mount and when projectId changes
   * Note: This is now disabled to allow manual loading on tab click
   */
  // useEffect(() => {
  //   if (projectId) {
  //     fetchApiKeys();
  //   }
  // }, [projectId, fetchApiKeys]);

  return {
    // State
    apiKeys,
    loading,
    error,
    pagination,

    // Actions
    fetchApiKeys,
    createApiKey,
    updateApiKey,
    deleteApiKey,
    regenerateApiKey,
    getApiKeyById,
    clearError,
  };
};
